import React, { useContext, useEffect, useState } from 'react'
import { config } from '../modules/app/config'
import { useAnalytics } from './useAnalytics'
import { useCurrentUserQuery } from './useCurrentUser'
import { useCreateGrowsurfParticipantMutation } from '../graphql/generated/graphql'

declare global {
    interface Window {
        growsurf: {
            init: (i?: Object) => Promise<any>
            initElements: () => void
            subscribe: (e: string, cb: Function) => void
            triggerReferral: (data: any) => Promise<any>
            addParticipant: (data: any) => Promise<any>
            getParticipant: () => Promise<any>
            getReferrerId: () => string | null
        }
    }
}

export const useGrowsurf = () => {
    const insertGrowsurfScript = () => {
        const script = document.createElement('script')
        script.src = 'https://growsurf.com/growsurf.js?v=2.0.0'
        script.setAttribute('grsf-campaign', config.growsurfCampaignId)
        script.async = true
        document.head.appendChild(script)
    }

    useEffect(() => {
        if (!window.growsurf) {
            window.addEventListener('grsfReady', handleGrowSurfReady)
            insertGrowsurfScript()
        }
    }, [])

    const { trackAnalyticsEvent } = useAnalytics()
    const { growsurf, me, refetch } = useCurrentUserQuery()
    const [loaded, setLoaded] = useState(false)
    const [createParticipant, { loading: createLoading }] = useCreateGrowsurfParticipantMutation()

    const ensureParticipant = () => {
        if (me && !growsurf?.participant && !createLoading) {
            createParticipant({
                variables: {
                    referrerId: window.growsurf?.getReferrerId(),
                },
            }).then(() => refetch())
        }
    }

    const handleGrowSurfReady = async () => {
        // console.log('growsurf ready')
        // remove our listener
        window.removeEventListener('grsfReady', handleGrowSurfReady, false)
        // auto authenticate the participant

        // Subscribe to a `signup` event type
        window.growsurf.subscribe('signup', (participant) => {
            trackAnalyticsEvent('Growsurf Signup', participant)
        })

        // Subscribe to a `share` event type
        window.growsurf.subscribe('share', (data) => {
            trackAnalyticsEvent('Growsurf Share', data)
        })

        // Subscribe to a `referral` event type
        window.growsurf.subscribe('referral', (participant) => {
            trackAnalyticsEvent('Growsurf Referral', participant)
        })

        // Subscribe to an `invite` event type
        window.growsurf.subscribe('invite', (participant) => {
            trackAnalyticsEvent('Growsurf Invite', participant)
        })

        setLoaded(true)
    }

    const init = () => {
        // console.log('Growsurf init')

        //https://docs.growsurf.com/integrate/javascript-web-api/api-reference#initialize-reinitialize-growsurf
        window.growsurf
            ?.init({
                email: me?.email,
                hash: growsurf.secret,
            })
            ?.then((result) => {
                // console.log('Init done')
            })
            .catch((err) => {
                console.error('Growsurf init error', err)
            })
    }

    useEffect(() => {
        if (!me && loaded) {
            return
        }
        ensureParticipant()
    }, [me, loaded])

    return {
        growsurf: loaded ? window.growsurf : {},
        init,
        loaded,
    }
}

const GrowsurfState = React.createContext<any>(undefined)

export const GrowsurfProvider: React.FC = ({ children }) => {
    const { growsurf, loaded, init } = useGrowsurf()

    const value = {
        growsurf,
        loaded,
        init,
    }

    return <GrowsurfState.Provider value={value}>{children}</GrowsurfState.Provider>
}

export const useGrowsurfContext = () => {
    return useContext(GrowsurfState)
}
